import React, { useState, useEffect } from 'react';
import { Lock, ShieldCheck, AlertTriangle, ChefHat, QrCode, Save, Store, Receipt, Trash2, Plus, Eye, EyeOff, UserCheck } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const Toggle = ({ checked, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors focus:outline-none ${checked ? 'bg-green-500' : 'bg-gray-300'}`}
  >
    <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-8' : 'translate-x-1'}`} />
  </button>
);

const defaultProfile = {
  restaurant_name: '', address: '', phone: '', gstin: '', fssai: '', logo_url: '',
  gst_enabled: false, gst_rate: 5, gst_inclusive: false,
  service_charge_enabled: false, service_charge_rate: 5,
  packaging_charge_enabled: false, packaging_charge_amount: 20,
  delivery_charge_enabled: false, delivery_charge_amount: 40,
  default_discount_enabled: false, default_discount_type: 'percent', default_discount_value: 0,
};

const Settings = () => {
  const { user } = useAuth();
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [saving, setSaving] = useState(false);
  const [chefs, setChefs] = useState([]);
  const [newChefForm, setNewChefForm] = useState({ email: '', password: '', confirm_password: '' });
  const [showNewChefPassword, setShowNewChefPassword] = useState(false);
  const [chefAdding, setChefAdding] = useState(false);
  const [deletingChefId, setDeletingChefId] = useState(null);
  const [upiForm, setUpiForm] = useState({ upi_id: '', restaurant_name: '' });
  const [upiSaving, setUpiSaving] = useState(false);
  const [profile, setProfile] = useState(defaultProfile);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);

  useEffect(() => {
    fetchChefs();
    fetchUpi();
    fetchProfile();
  }, []);

  const fetchChefs = async () => {
    try {
      const res = await api.getUsers();
      setChefs(res.data.filter(u => u.role === 'chef'));
    } catch {}
  };

  const fetchUpi = async () => {
    try {
      const res = await api.getUpiSettings();
      setUpiForm({ upi_id: res.data.upi_id || '', restaurant_name: res.data.restaurant_name || '' });
    } catch {}
  };

  const fetchProfile = async () => {
    setProfileLoading(true);
    try {
      const res = await api.getRestaurantProfile();
      setProfile({ ...defaultProfile, ...res.data });
    } catch {
      toast.error('Failed to load restaurant profile. Please refresh the page.');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleSaveUpi = async () => {
    if (!upiForm.upi_id.trim()) return toast.error('UPI ID is required');
    if (!upiForm.upi_id.includes('@')) return toast.error('Enter a valid UPI ID (e.g. name@ybl)');
    setUpiSaving(true);
    try {
      await api.saveUpiSettings({ upi_id: upiForm.upi_id.trim(), restaurant_name: upiForm.restaurant_name.trim() });
      toast.success('UPI settings saved successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save UPI settings');
    } finally { setUpiSaving(false); }
  };

  const handleSaveProfile = async () => {
    setProfileSaving(true);
    try {
      await api.saveRestaurantProfile(profile);
      toast.success('Restaurant profile saved successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save profile');
    } finally { setProfileSaving(false); }
  };

  const handleChangePassword = async () => {
    if (!form.current_password || !form.new_password || !form.confirm_password) return toast.error('All fields are required');
    if (form.new_password !== form.confirm_password) return toast.error('New passwords do not match');
    if (form.new_password.length < 6) return toast.error('New password must be at least 6 characters');
    if (form.new_password === form.current_password) return toast.error('New password must be different from current password');
    setSaving(true);
    try {
      await api.changePassword(form.current_password, form.new_password);
      toast.success('Password changed successfully');
      setForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally { setSaving(false); }
  };

  const handleAddChef = async () => {
    if (!newChefForm.email.trim()) return toast.error('Email is required');
    if (!newChefForm.email.includes('@')) return toast.error('Enter a valid email address');
    if (!newChefForm.password) return toast.error('Password is required');
    if (newChefForm.password.length < 6) return toast.error('Password must be at least 6 characters');
    if (newChefForm.password !== newChefForm.confirm_password) return toast.error('Passwords do not match');
    setChefAdding(true);
    try {
      await api.registerUser({ email: newChefForm.email.trim(), password: newChefForm.password, role: 'chef' });
      toast.success(`Chef ${newChefForm.email} added successfully`);
      setNewChefForm({ email: '', password: '', confirm_password: '' });
      fetchChefs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add chef');
    } finally { setChefAdding(false); }
  };

  const handleDeleteChef = async (chefId, chefEmail) => {
    if (!window.confirm(`Remove chef account for ${chefEmail}? They will no longer be able to log in.`)) return;
    setDeletingChefId(chefId);
    try {
      await api.deleteUser(chefId);
      toast.success(`Chef ${chefEmail} removed`);
      fetchChefs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove chef');
    } finally { setDeletingChefId(null); }
  };

  const p = (field) => profile[field];
  const setP = (field, value) => setProfile(prev => ({ ...prev, [field]: value }));

  const inputClass = "w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#E65100]";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";

  return (
    <div className="p-6">
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <h2 className="text-3xl font-bold text-gray-900">Settings</h2>
          <p className="text-gray-600 mt-1">Manage your restaurant profile, billing charges and account security</p>
        </div>

        {/* Security warning */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold text-yellow-800">Change your default admin password</p>
            <p className="text-sm text-yellow-700 mt-1">
              If you are still using <span className="font-mono bg-yellow-100 px-1 rounded">admin123</span>, change it immediately to keep your account secure.
            </p>
          </div>
        </div>

        {/* ── RESTAURANT PROFILE ── */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2 bg-orange-100 rounded-lg"><Store className="w-5 h-5 text-[#E65100]" /></div>
            <div>
              <p className="font-semibold text-gray-900">Restaurant Profile</p>
              <p className="text-sm text-gray-500">Appears on printed bills and receipts</p>
            </div>
          </div>
          {profileLoading ? (
            <div className="text-center py-6 text-gray-400 text-sm">Loading profile...</div>
          ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Restaurant Name</label>
                <input className={inputClass} placeholder="e.g. Sharma Dhaba" value={p('restaurant_name')} onChange={e => setP('restaurant_name', e.target.value)} />
              </div>
              <div>
                <label className={labelClass}>Phone Number</label>
                <input className={inputClass} placeholder="e.g. 9307190357" value={p('phone')} onChange={e => setP('phone', e.target.value)} />
              </div>
            </div>
            <div>
              <label className={labelClass}>Logo URL <span className="text-gray-400 font-normal">(optional — paste image link)</span></label>
              <input className={inputClass} placeholder="https://your-restaurant.com/logo.png" value={p('logo_url')} onChange={e => setP('logo_url', e.target.value)} />
              {p('logo_url') && (
                <div className="mt-2 flex items-center gap-3">
                  <img src={p('logo_url')} alt="Logo preview" className="h-14 w-auto object-contain border border-gray-200 rounded-lg p-1" onError={e => e.target.style.display='none'} />
                  <p className="text-xs text-gray-500">Logo preview — appears on printed receipts</p>
                </div>
              )}
            </div>
            <div>
              <label className={labelClass}>Address</label>
              <input className={inputClass} placeholder="Full restaurant address" value={p('address')} onChange={e => setP('address', e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>GSTIN <span className="text-gray-400 font-normal">(optional)</span></label>
                <input className={inputClass} placeholder="e.g. 27XXXXX1234X1ZX" value={p('gstin')} onChange={e => setP('gstin', e.target.value.toUpperCase())} />
              </div>
              <div>
                <label className={labelClass}>FSSAI Number <span className="text-gray-400 font-normal">(optional)</span></label>
                <input className={inputClass} placeholder="14-digit FSSAI number" value={p('fssai')} onChange={e => setP('fssai', e.target.value)} />
              </div>
            </div>
          </div>
          )}
        </div>

        {/* ── GST & CHARGES ── */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2 bg-blue-100 rounded-lg"><Receipt className="w-5 h-5 text-blue-600" /></div>
            <div>
              <p className="font-semibold text-gray-900">GST & Charges</p>
              <p className="text-sm text-gray-500">These auto-apply to every bill. Admin can override per bill.</p>
            </div>
          </div>
          <div className="space-y-4">

            {/* GST */}
            <div className="border border-gray-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="font-medium text-gray-900 text-sm">GST (CGST + SGST)</p>
                  <p className="text-xs text-gray-500">Split equally as CGST & SGST on receipt</p>
                </div>
                <Toggle checked={p('gst_enabled')} onChange={v => setP('gst_enabled', v)} />
              </div>
              {p('gst_enabled') && (
                <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-gray-100">
                  <div>
                    <label className={labelClass}>GST Rate</label>
                    <select className={inputClass} value={p('gst_rate')} onChange={e => setP('gst_rate', parseFloat(e.target.value))}>
                      <option value={5}>5% (Most restaurants)</option>
                      <option value={12}>12%</option>
                      <option value={18}>18% (AC restaurants)</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>GST Type</label>
                    <select className={inputClass} value={p('gst_inclusive') ? 'inclusive' : 'exclusive'} onChange={e => setP('gst_inclusive', e.target.value === 'inclusive')}>
                      <option value="exclusive">Add on top of price</option>
                      <option value="inclusive">Already included in price</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* Service Charge */}
            <div className="border border-gray-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="font-medium text-gray-900 text-sm">Service Charge</p>
                  <p className="text-xs text-gray-500">Applied on dine-in orders</p>
                </div>
                <Toggle checked={p('service_charge_enabled')} onChange={v => setP('service_charge_enabled', v)} />
              </div>
              {p('service_charge_enabled') && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <label className={labelClass}>Service Charge Rate (%)</label>
                  <input type="number" min="0" max="20" step="0.5" className={inputClass} value={p('service_charge_rate')} onChange={e => setP('service_charge_rate', parseFloat(e.target.value) || 0)} />
                </div>
              )}
            </div>

            {/* Packaging Charge */}
            <div className="border border-gray-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="font-medium text-gray-900 text-sm">Packaging Charge</p>
                  <p className="text-xs text-gray-500">Flat amount — applied on delivery orders</p>
                </div>
                <Toggle checked={p('packaging_charge_enabled')} onChange={v => setP('packaging_charge_enabled', v)} />
              </div>
              {p('packaging_charge_enabled') && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <label className={labelClass}>Packaging Charge Amount (₹)</label>
                  <input type="number" min="0" className={inputClass} value={p('packaging_charge_amount')} onChange={e => setP('packaging_charge_amount', parseFloat(e.target.value) || 0)} />
                </div>
              )}
            </div>

            {/* Delivery Charge */}
            <div className="border border-gray-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="font-medium text-gray-900 text-sm">Delivery Charge</p>
                  <p className="text-xs text-gray-500">Flat amount — applied on delivery orders</p>
                </div>
                <Toggle checked={p('delivery_charge_enabled')} onChange={v => setP('delivery_charge_enabled', v)} />
              </div>
              {p('delivery_charge_enabled') && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <label className={labelClass}>Delivery Charge Amount (₹)</label>
                  <input type="number" min="0" className={inputClass} value={p('delivery_charge_amount')} onChange={e => setP('delivery_charge_amount', parseFloat(e.target.value) || 0)} />
                </div>
              )}
            </div>

          </div>

            {/* Default Discount */}
            <div className="border border-gray-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="font-medium text-gray-900 text-sm">Default Discount</p>
                  <p className="text-xs text-gray-500">Pre-fills discount on every new bill (admin can change per bill)</p>
                </div>
                <Toggle checked={p('default_discount_enabled')} onChange={v => setP('default_discount_enabled', v)} />
              </div>
              {p('default_discount_enabled') && (
                <div className="mt-3 pt-3 border-t border-gray-100 grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass}>Discount Type</label>
                    <select className={inputClass} value={p('default_discount_type')} onChange={e => setP('default_discount_type', e.target.value)}>
                      <option value="percent">Percentage (%)</option>
                      <option value="flat">Flat Amount (₹)</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Discount Value</label>
                    <input type="number" min="0" className={inputClass} placeholder={p('default_discount_type') === 'percent' ? 'e.g. 10' : 'e.g. 50'} value={p('default_discount_value')} onChange={e => setP('default_discount_value', parseFloat(e.target.value) || 0)} />
                  </div>
                </div>
              )}
            </div>

          <Button onClick={handleSaveProfile} disabled={profileSaving} className="w-full mt-5 bg-[#E65100] hover:bg-[#D84800] text-white h-11 font-semibold">
            <Save className="w-4 h-4 mr-2" />
            {profileSaving ? 'Saving...' : 'Save Restaurant Profile & Charges'}
          </Button>
        </div>

        {/* ── UPI ── */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2 bg-purple-100 rounded-lg"><QrCode className="w-5 h-5 text-purple-600" /></div>
            <div>
              <p className="font-semibold text-gray-900">UPI Payment Settings</p>
              <p className="text-sm text-gray-500">Enter your UPI ID to accept payments via QR code on bills</p>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <label className={labelClass}>UPI ID *</label>
              <input type="text" placeholder="e.g. restaurantname@ybl or 9999999999@paytm" value={upiForm.upi_id} onChange={e => setUpiForm({ ...upiForm, upi_id: e.target.value })} className={inputClass} />
              <p className="text-xs text-gray-400 mt-1">Find your UPI ID in GPay, PhonePe, Paytm or any UPI app</p>
            </div>
            {upiForm.upi_id && upiForm.upi_id.includes('@') && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 flex items-center gap-3">
                <QrCode className="w-5 h-5 text-purple-600 shrink-0" />
                <p className="text-sm font-medium text-purple-800">QR will be auto-generated for each bill with exact amount</p>
              </div>
            )}
            <Button onClick={handleSaveUpi} disabled={upiSaving} className="w-full bg-purple-600 hover:bg-purple-700 text-white h-11 font-semibold">
              <Save className="w-4 h-4 mr-2" />
              {upiSaving ? 'Saving...' : 'Save UPI Settings'}
            </Button>
          </div>
        </div>

        {/* ── ADMIN ACCOUNT ── */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-blue-100 rounded-lg"><ShieldCheck className="w-5 h-5 text-blue-600" /></div>
            <div>
              <p className="font-semibold text-gray-900">Admin Account</p>
              <p className="text-sm text-gray-500">Your current login details</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><p className="text-gray-500 mb-1">Email</p><p className="font-medium text-gray-900">{user?.email}</p></div>
            <div><p className="text-gray-500 mb-1">Role</p><p className="font-medium text-gray-900 capitalize">{user?.role}</p></div>
          </div>
        </div>

        {/* ── CHANGE ADMIN PASSWORD ── */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2 bg-orange-100 rounded-lg"><Lock className="w-5 h-5 text-[#E65100]" /></div>
            <div>
              <p className="font-semibold text-gray-900">Change Admin Password</p>
              <p className="text-sm text-gray-500">Use a strong password with letters, numbers and symbols</p>
            </div>
          </div>
          <div className="space-y-4">
            <div><label className={labelClass}>Current Password</label><input type="password" placeholder="Enter your current password" value={form.current_password} onChange={e => setForm({ ...form, current_password: e.target.value })} className={inputClass} /></div>
            <div><label className={labelClass}>New Password</label><input type="password" placeholder="Enter new password (min 6 characters)" value={form.new_password} onChange={e => setForm({ ...form, new_password: e.target.value })} className={inputClass} /></div>
            <div><label className={labelClass}>Confirm New Password</label><input type="password" placeholder="Re-enter new password" value={form.confirm_password} onChange={e => setForm({ ...form, confirm_password: e.target.value })} className={inputClass} /></div>
            <Button onClick={handleChangePassword} disabled={saving} className="w-full bg-[#E65100] hover:bg-[#D84800] text-white h-11 font-semibold">
              {saving ? 'Changing Password...' : 'Change Admin Password'}
            </Button>
          </div>
        </div>

        {/* ── CHEF MANAGEMENT ── */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2 bg-green-100 rounded-lg"><ChefHat className="w-5 h-5 text-green-600" /></div>
            <div>
              <p className="font-semibold text-gray-900">Chef Accounts</p>
              <p className="text-sm text-gray-500">Add or remove chef logins for your kitchen staff</p>
            </div>
          </div>

          {/* Existing Chefs List */}
          <div className="mb-5">
            {chefs.length === 0 ? (
              <div className="text-center py-6 border border-dashed border-gray-200 rounded-xl text-gray-400 text-sm">
                No chef accounts yet — add one below
              </div>
            ) : (
              <div className="space-y-2">
                {chefs.map(chef => (
                  <div key={chef.id} className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="p-1.5 bg-green-100 rounded-lg"><UserCheck className="w-4 h-4 text-green-600" /></div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{chef.email}</p>
                        <p className="text-xs text-green-600 font-medium">Chef</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteChef(chef.id, chef.email)}
                      disabled={deletingChefId === chef.id}
                      className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition disabled:opacity-50"
                      title="Remove chef"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add New Chef Form */}
          <div className="border border-gray-200 rounded-xl p-4 bg-gray-50">
            <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Plus className="w-4 h-4 text-green-600" /> Add New Chef
            </p>
            <div className="space-y-3">
              <div>
                <label className={labelClass}>Chef Email</label>
                <input
                  type="email"
                  placeholder="e.g. chef@yourrestaurant.com"
                  value={newChefForm.email}
                  onChange={e => setNewChefForm({ ...newChefForm, email: e.target.value })}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Password</label>
                <div className="relative">
                  <input
                    type={showNewChefPassword ? 'text' : 'password'}
                    placeholder="Min 6 characters"
                    value={newChefForm.password}
                    onChange={e => setNewChefForm({ ...newChefForm, password: e.target.value })}
                    className={inputClass}
                  />
                  <button type="button" onClick={() => setShowNewChefPassword(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showNewChefPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className={labelClass}>Confirm Password</label>
                <input
                  type="password"
                  placeholder="Re-enter password"
                  value={newChefForm.confirm_password}
                  onChange={e => setNewChefForm({ ...newChefForm, confirm_password: e.target.value })}
                  className={inputClass}
                />
              </div>
              <Button
                onClick={handleAddChef}
                disabled={chefAdding}
                className="w-full bg-green-600 hover:bg-green-700 text-white h-11 font-semibold"
              >
                <Plus className="w-4 h-4 mr-2" />
                {chefAdding ? 'Adding Chef...' : 'Add Chef Account'}
              </Button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Settings;
